import { forwardRef, isValidElement } from 'react';
import Img from 'react-image';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { HoverCard, Stack, Title } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import Icon from '@ui/components/Icon/Icon';
import FeaturePaywallPlanWrapper from '@workspaces/components/FeaturePaywall/FeaturePaywall';
import FloatingBox from '../FloatingBox/FloatingBox';
import { PRIMARY_BASE } from '../../styles/colors';
import WithOptionalTooltip from '../WithOptionalTooltip/WithOptionalTooltip';
import './FloatingMenu.scss';

/* eslint-disable react/no-array-index-key */

const FloatingMenu = forwardRef( ( {
	items, showArrow, onItemClick, className, footer, style
}, ref ) => {
	const renderItem = item => (
		<>
			{item.icon
			&& (
				<Icon
					className={item.className || 'item-icon'}
					icon={item.icon}
					size={item.size || 15}
					color={item.color || PRIMARY_BASE}
				/>
			)}
			{item.image
			&& (
				<Img
					src={item.image}
					className="img_style"
				/>
			)}
			<div className="name-description">
				<h5 style={{ color: item.textColor }}>{item.name || item}</h5>
				<p>{item.description}</p>
			</div>
			{item.control}
			{item.badge
			&& (
				<div className="badge">
					{item.badge.text}
				</div>
			)}
		</>
	);

	const renderButton = ( item, index ) => {
		if ( item.control ) {
			return (
				<div className={classNames( 'control-item', item.classNames, { with_description: !!item.description } )}>
					{renderItem( item )}
				</div>
			);
		}

		return (
			<button
				className={classNames( item.classNames, {
					with_description: !!item.description,
					grey_out: item.greyOut
				} )}
				onClick={event => ( !item.greyOut ? onItemClick( index, event ) : undefined )}
			>
				{isValidElement( item ) ? item : renderItem( item )}
			</button>
		);
	};

	const renderPaywallButton = ( item, index ) => (
		<FeaturePaywallPlanWrapper feature={item.paywall?.feature} workspace={item.paywall?.associatedWorkspace}>
			{ () => renderButton( item, index ) }
		</FeaturePaywallPlanWrapper>
	);

	return (
		<FloatingBox style={style} showArrow={showArrow} className={className}>
			<ul ref={ref} className="FloatingMenu">
				{items.map( ( item, index ) => (
					<>
						{item.startsNewGroup && index > 0 && <div className="group-divider" />}
						<li key={index}>
							<HoverCard position="right-start" withinPortal width={240} offset={4} disabled={!item.hoverCard}>
								<HoverCard.Target>
									<WithOptionalTooltip tooltip={item.tooltip}>
										{item.paywall
											? renderPaywallButton( item, index )
											: renderButton( item, index )}
									</WithOptionalTooltip>
								</HoverCard.Target>
								<HoverCard.Dropdown bg="space.9" style={{ border: 0 }}>
									<Stack spacing={4}>
										{item.hoverCard?.title && (
											<Title order={5} color="white">{item.hoverCard?.title}</Title>
										)}
										{item.hoverCard?.description && (
											<Text color="white" variant="footnote">
												{item.hoverCard?.description}
											</Text>
										)}
										{item.hoverCard?.footer && (
											<Text color="white" variant="caption" italic>
												{item.hoverCard?.footer}
											</Text>
										)}
									</Stack>
								</HoverCard.Dropdown>
							</HoverCard>
						</li>
					</>
				) )}
			</ul>
			{isValidElement( footer )
				&& <div>{footer}</div>}
		</FloatingBox>
	);
} );

FloatingMenu.propTypes = {
	items: PropTypes.arrayOf( PropTypes.oneOfType( [
		PropTypes.shape( {
			name: PropTypes.string,
			description: PropTypes.string,
			icon: PropTypes.string,
			color: PropTypes.string,
			size: PropTypes.number,
			image: PropTypes.string,
			classNames: PropTypes.string,
			textColor: PropTypes.string
		} ),
		PropTypes.node
	] ) ),
	showArrow: PropTypes.bool,
	onItemClick: PropTypes.func,
	className: PropTypes.string,
	footer: PropTypes.node,
	style: PropTypes.any
};

FloatingMenu.defaultProps = {
	items: [],
	showArrow: true,
	onItemClick: () => {},
	className: undefined,
	footer: undefined,
	style: {}
};

FloatingMenu.displayName = 'FloatingMenu';

export default FloatingMenu;
